/**
 * aggregation.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { FormControl, Validators } from "@angular/forms";

import { TypedDataHolder } from "../common/data/typed-data";
import {
    AggregationSubjectContextRequest,
    AggregatorReportResponse,
    AggregatorStatusRequest,
    AggregatorStatusResponse,
    CancelAggregationRequest,
    CreateAggregatorResponse,
    PauseAggregatorRequest,
    ResumeAggregatorRequest
} from "../server/messages/aggregation/";
import { AggregationFilterNode }
    from "../server/messages/aggregation/start-aggregator-request";
import { ReplyVerifyingMessage }
    from "../server/messages/general/reply-message";
import { SubjectReference } from "../subjects/subject-reference";
import { assertIndexInto } from "../support/utilities/assertions";
import { Aggregator } from "./aggregator";
import { ConstantNode, FilterNode, OperatorNode, UndefinedNode, VariableNode }
    from "./filter-node";
import { Equals, Is, Operators } from "./operators";
import { AggregationSchema } from "./schemas";
import { ActivityType, BasicActivityCommand, FilterOptions, ToolboxActivity }
    from "./toolbox-activity";
import {
    AggVariable,
    ContextResult,
    ContextResultSummary,
    FrequencyData,
    FrequencyResult,
    Variable
} from "./typed-parameter";

/**
 * An {@link Aggregation} is a specific task that may be configured and run by
 * an aggregator agent. It represents a single "query" of the aggregation
 * engine. In the user-facing lingo, these units are distinguished as "tasks".
 */
export class Aggregation extends ToolboxActivity
{
    public targetSelector : FormControl;
    public requiresStateVariable : boolean = false;

    constructor (
        public id : number,
        public filterOptions : AggVariable[] = [],
        public targetVariableOptions : AggVariable[] = [],
        public parent : Aggregator
    )
    {
        super();
        this.targetSelector = new FormControl(null, Validators.required);
        this.configurationControls = [this.targetSelector];
        this.title = `${this.parent.name} ${++this.parent.childCounter}`;
        this.requiresStateVariable = parent.requiresStateVariable;
        this.targetVariableOptions.sort(
            (a : AggVariable, b : AggVariable) =>
                (a.displayName || "").localeCompare(b.displayName));
    }

    public standardResults : AggVariable[] = [];
    public frequencyResults : FrequencyResult[] = [];
    public contextResults : ContextResult[] = [];

    public clearResults () : void
    {
        this.standardResults = [];
        this.frequencyResults = [];
        this.contextResults = [];
    }

    // Convenience flag to signal to the component that none of the 3 results
    // arrays contain values.
    public noResults : boolean = false;

    public static fromOptions (
        rsp : CreateAggregatorResponse,
        parent : Aggregator
    ) : Aggregation
    {
        return new Aggregation(
            rsp.aggregationId,
            rsp.filterOptions,
            rsp.targetVariableOptions,
            parent
        );
    }

    public readSchema (schema : AggregationSchema) : void
    {
        this.targetSelector.setValue(schema.targetVariable);
        console.log("target variable", schema.targetVariable);
        if (schema.filter.nodeType === "operator")
        {
            this.filter = OperatorNode.fromSchema(
                schema.filter as AggregationFilterNode,
                this.filterOptions as any[],
                true);
        }
        else
        {
            // Either there's no filter or the filter isn't rooted by an
            // operator, which is an illegal configuration.
        }
    }

    public readMessageReport (arm : AggregatorReportResponse) : void
    {
        this.readCompletion(arm);

        if (arm.results.length === 0)
        {
            this.noResults = true;
        }
        else
        {
            arm.results.forEach((result : AggVariable) => {
                if (result.type === "frequency")
                {
                    this.frequencyResults.push(result as FrequencyResult);
                }
                else if (result.type === "subject context")
                {
                    const contextHolder : ContextResult =
                        result as ContextResult;
                    if (result.cardinality === 1)
                    {
                        contextHolder.columns =
                            (contextHolder.value as ContextResultSummary)
                                .summary.map((d : {name? : string}) => d.name);
                        // if there's an item ID defined, create a link object
                        if (Number.isInteger(contextHolder.itemId))
                        {
                            contextHolder.openLink = [
                                "/subject/agg/",
                                this.id,
                                contextHolder.itemId
                            ];
                        }
                        // TODO: note that we don't create the link for nonatoms
                    }
                    else if ((contextHolder.value as any[]).length)
                    {
                        contextHolder.columns =
                            (contextHolder.value as ContextResultSummary[])[0]
                                .summary.map((d : {name? : string}) => d.name);
                    }
                    else
                    {
                        contextHolder.columns = [];
                    }
                    this.contextResults.push(result as ContextResult);
                }
                else
                {
                    this.standardResults.push(result);
                }
            });
        }
    }

    public requestFor (action : BasicActivityCommand) : ReplyVerifyingMessage
    {
        switch (action)
        {
            case "cancel":
                return new CancelAggregationRequest(this.id);
            case "pause":
                return new PauseAggregatorRequest(this.id);
            case "resume":
                return new ResumeAggregatorRequest(this.id);
            case "status":
                return new AggregatorStatusRequest(this.id);
            default:
                throw new Error(`Unsupported activity request: ${action}`);
        }
    }

    public buildReportRef (r : number, itemId : number)
        : SubjectReference
    {
        assertIndexInto(r, this.contextResults);
        const result : ContextResult = this.contextResults[r];

        return {
            id: itemId,
            host: result,
            type: "aggregation",
            request: new AggregationSubjectContextRequest(
                this.id, itemId),
            priority: 30
        };
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/aggregation.ts